import { useEffect, useRef, useState } from 'react';
import { NpcEncounterServiceModule } from '../services/host/NpcEncounterServiceModule';

/**
 * A8r (2026-06-04) — NPC encounter registry hook.
 *
 * Hydrates a per-campaign Set of encountered NPC ids on mount, then
 * auto-records new encounters when the scenario navigates to a paragraph
 * whose `encounter_paragraph_first_appearance` matches one or more NPCs.
 *
 * Per the prep brief D2/D4:
 *   - Auto-record on paragraph change (no GM input needed; scenario JSON
 *     already carries `encounter_paragraph_first_appearance` per NPC)
 *   - Best-effort recording: a failed POST leaves the local set unchanged
 *     and surfaces `error`; the next paragraph navigation re-triggers
 *     the effect for a retry
 *
 * Idempotency belt-and-suspenders:
 *   - Local Set check BEFORE fetch (`encounteredIdsRef`) prevents
 *     duplicate POST for an already-known NPC
 *   - Server UNIQUE constraint on (npc_id, scenario_id) serializes
 *     concurrent POSTs and returns the existing row
 *
 * Consumer pattern (ScenarioRunner):
 *   const { encounteredIds } = useNpcEncounters({
 *     campaignId: campaign.id,
 *     scenarioId: scenario.source_manifest ?? 'unknown',
 *     candidateNpcs: npcLookup.all,
 *     currentParagraph: state.view.kind === 'paragraph' ? state.view.number : null,
 *     encounterMap: buildEncounterMap(scenario),
 *   });
 *
 *   const dossierNpcs = npcLookup.all
 *     .filter((n) => encounteredIds.has(n.id))
 *     .map(...);
 */
export interface UseNpcEncountersOptions {
  campaignId: string;
  scenarioId: string;
  /** Current paragraph number, or null when view is briefing / ending. */
  currentParagraph: number | null;
  /** Paragraph number → list of npc ids whose
   *  `encounter_paragraph_first_appearance` matches that paragraph. */
  encounterMap: ReadonlyMap<number, readonly string[]>;
  /** Factory injected for tests. Defaults to constructing a real
   *  NpcEncounterServiceModule (which uses real fetch + localStorage). */
  serviceFactory?: () => NpcEncounterServiceModule;
}

export interface UseNpcEncountersResult {
  encounteredIds: ReadonlySet<string>;
  isLoading: boolean;
  error: string | null;
}

export function useNpcEncounters({
  campaignId,
  scenarioId,
  currentParagraph,
  encounterMap,
  serviceFactory,
}: UseNpcEncountersOptions): UseNpcEncountersResult {
  const [encounteredIds, setEncounteredIds] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Mirror latest encountered ids into a ref so the paragraph-change
  // effect can read them synchronously without waiting for React's
  // setState callback (same A6e lesson — React 18's setState updater
  // runs asynchronously, so a setState-based read would race).
  const encounteredIdsRef = useRef<Set<string>>(new Set());

  // The service instance is stable across renders so tests can spy on
  // method calls. Created lazily via the factory.
  const serviceRef = useRef<NpcEncounterServiceModule | null>(null);
  if (serviceRef.current === null) {
    serviceRef.current = serviceFactory
      ? serviceFactory()
      : new NpcEncounterServiceModule();
  }

  // Initial sync (or re-sync on campaign/scenario change).
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    const service = serviceRef.current;
    if (service === null) {
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }
    void (async () => {
      try {
        const registry = await service.getRegistry(campaignId);
        if (cancelled) return;
        // Scope the in-memory set to encounters from THIS scenario.
        // (Backend may store encounters from prior scenarios on the
        // same campaign DB; we only care about the active one for the
        // dossier wire.)
        const ids = new Set(
          registry.encounters
            .filter((e) => e.scenario_id === scenarioId)
            .map((e) => e.npc_id),
        );
        encounteredIdsRef.current = ids;
        setEncounteredIds(ids);
        setIsLoading(false);
      } catch (exc) {
        if (cancelled) return;
        const message = exc instanceof Error ? exc.message : String(exc);
        setError(message);
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignId, scenarioId]);

  // Auto-record encounters when the paragraph changes.
  useEffect(() => {
    if (currentParagraph === null) return;
    const candidates = encounterMap.get(currentParagraph) ?? [];
    const newIds = candidates.filter(
      (id) => !encounteredIdsRef.current.has(id),
    );
    if (newIds.length === 0) return;

    let cancelled = false;
    const service = serviceRef.current;
    if (service === null) return;

    void (async () => {
      await Promise.all(
        newIds.map(async (npcId) => {
          try {
            await service.recordEncounter(
              campaignId,
              npcId,
              scenarioId,
              currentParagraph,
            );
            if (cancelled) return;
            // Merge into both ref + state. Building a new Set each
            // time keeps React's reference-equality change detection
            // happy and avoids accidentally mutating a state value.
            const next = new Set(encounteredIdsRef.current);
            next.add(npcId);
            encounteredIdsRef.current = next;
            setEncounteredIds(next);
          } catch (exc) {
            if (cancelled) return;
            // Best-effort: local set stays as it was; the next
            // paragraph navigation will retry.
            const message = exc instanceof Error ? exc.message : String(exc);
            setError(message);
          }
        }),
      );
    })();

    return () => {
      cancelled = true;
    };
    // The effect intentionally does NOT include `encounteredIds` in its
    // deps — we read via `encounteredIdsRef.current` so the effect
    // doesn't re-run on every successful POST. encounterMap is expected
    // to be stable across renders (caller wraps in useMemo).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentParagraph, campaignId, scenarioId, encounterMap]);

  return {
    encounteredIds,
    isLoading,
    error,
  };
}
